import { useLayoutEffect } from 'react'
import { MemoryRouter, Prompt, Route, Router, useLocation } from 'react-router-dom'

import { SettingsModalEmbedProvider } from 'components/settings_modal_embed_context'
import changeSet from 'core/change_set'

import { SETTINGS_TAB_SEARCH_PARAM, mergeSettingsSearch } from './constants'
import { memoryPathnameToTabSlug } from './settings_modal_memory_tabs'
import { confirmDiscardIfDirty } from './use_settings_modal_url_normalization'

const getSettingsModalEmbedValue = ({
  browserHistory,
  browserLocation,
}) => ({
  embedded: true,
  browserHistory,
  browserLocation,
  getSettingsModalTarget: (section, settingsTab) => (
    browserHistory != null && browserLocation?.pathname ? {
      pathname: browserLocation.pathname,
      search: mergeSettingsSearch(section, browserLocation.search ?? '', settingsTab),
      hash: browserLocation.hash ?? '',
    } : null
  ),
})

export const confirmMemoryRouteNavigation = (_message, callback) => {
  callback(confirmDiscardIfDirty())
}

/**
 * Keeps `settings_tab=<slug>` in the real browser URL in sync with the memory router pathname
 * (for shareable deep links to a tab within Login, Permissions, Security, etc.).
 */
const MemoryLocationToBrowserTabSync = ({
  settingsSection,
  browserHistory,
  browserLocation,
}) => {
  const { pathname } = useLocation()

  useLayoutEffect(() => {
    if (!settingsSection || !browserHistory || !browserLocation?.pathname) {
      return
    }
    const slug = memoryPathnameToTabSlug(settingsSection, pathname)
    if (slug == null) {
      return
    }
    const params = new URLSearchParams(browserLocation.search ?? '')
    if (params.get(SETTINGS_TAB_SEARCH_PARAM) === slug) {
      return
    }
    params.set(SETTINGS_TAB_SEARCH_PARAM, slug)
    const qs = params.toString()
    browserHistory.replace({
      pathname: browserLocation.pathname,
      search: `?${qs}`,
      hash: browserLocation.hash ?? '',
    })
  }, [
    pathname,
    settingsSection,
    browserHistory,
    browserLocation?.pathname,
    browserLocation?.search,
    browserLocation?.hash,
  ])

  return null
}

/**
 * Mounts a route-based settings page on an isolated MemoryRouter so nested paths match while the
 * browser URL stays on the host page (`?settings=…` & optional `settings_tab=…`).
 *
 * Two render modes:
 * - **`component` mode** — passes `component` to a single `<Route path={basePath} />`. Used by the
 *   default body resolver in `section_content_registry.js`.
 * - **`children` mode** — renders `children` directly inside the MemoryRouter (no auto-Route). Used
 *   by the desktop tabbed-header layout in `settings_modal.js`, which needs to mount a CUI
 *   `PageHeaderWithTabs` _outside_ the page's own `<Route>` so memory-router-aware `<RouterLink>`
 *   tab anchors can drive the embedded page. Pass either `component` or `children`, not both.
 *
 * `remountKey` controls when the underlying `MemoryRouter` remounts. Default is `basePath`, which
 * means tabs navigate in-memory and the inner page keeps its scroll/state. Pass a richer key (e.g.
 * `${basePath}:${initialPath}`) for sections like personal Calendar where tab anchors update the
 * browser URL _first_ and the memory stack must be reset to match — see the calendar row in
 * `MEMORY_ROUTE_BY_KEY` for the contract.
 *
 * @param {object} props
 * @param {string} props.basePath — `path` for the auto-`<Route>` and the default initial entry
 * @param {string} [props.initialPath] — first MemoryRouter entry (defaults to `basePath`); usually
 *   derived from `settings_tab=` via {@link ./settings_modal_memory_tabs.js}
 * @param {string} [props.remountKey] — React `key` for `<MemoryRouter>`; defaults to `basePath`
 * @param {import('history').MemoryHistory} [props.memoryHistory] — externally owned memory history
 *   for shells whose header and body need to share nested navigation.
 * @param {import('react').ComponentType<any>} [props.component] — body component (component mode)
 * @param {import('react').ReactNode} [props.children] — body tree (children mode)
 * @param {import('react-router').History} [props.browserHistory] — real app history (tab URL sync)
 * @param {import('react-router').Location} [props.browserLocation] — real app location (tab URL sync)
 * @param {string | null} [props.settingsSection] — effective `SETTINGS_SECTION` (tab URL sync)
 */
export const SettingsModalMemoryRouteShell = ({
  basePath,
  initialPath,
  remountKey,
  memoryHistory,
  component: Component,
  children,
  browserHistory,
  browserLocation,
  settingsSection,
}) => (
  <SettingsModalEmbedProvider
    value={getSettingsModalEmbedValue({
      browserHistory,
      browserLocation,
    })}
  >
    {memoryHistory != null ? (
      <Router key={remountKey ?? basePath} history={memoryHistory}>
        <Prompt message={changeSet.shouldBlockNavigation} />
        <MemoryLocationToBrowserTabSync
          settingsSection={settingsSection}
          browserHistory={browserHistory}
          browserLocation={browserLocation}
        />
        {Component != null
          ? <Route path={basePath} component={Component} />
          : children}
      </Router>
    ) : (
      <MemoryRouter
        key={remountKey ?? basePath}
        initialEntries={[initialPath ?? basePath]}
        initialIndex={0}
        getUserConfirmation={confirmMemoryRouteNavigation}
      >
        <Prompt message={changeSet.shouldBlockNavigation} />
        <MemoryLocationToBrowserTabSync
          settingsSection={settingsSection}
          browserHistory={browserHistory}
          browserLocation={browserLocation}
        />
        {Component != null
          ? <Route path={basePath} component={Component} />
          : children}
      </MemoryRouter>
    )}
  </SettingsModalEmbedProvider>
)
